import type React from 'react';
import { useState, useEffect, useCallback } from 'react';

export function Modal({
  isOpen,
  onClose,
  children,
  variant = 'bottom',
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  /** Animation direction: 'bottom' slides up (default), 'right' slides from right */
  variant?: 'bottom' | 'right';
}) {
  const [mounted, setMounted] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    } else if (mounted) {
      setAnimating(false);
      const t = setTimeout(() => setMounted(false), 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setAnimating(false);
    setTimeout(() => onClose(), 250);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  if (!mounted) return null;

  const closedTransform = variant === 'right' ? 'translateX(100%)' : 'translateY(24px)';
  const openTransform = 'translate(0)';

  return (
    <div
      className="absolute inset-0 z-20 transition-opacity duration-250"
      style={{ opacity: animating ? 1 : 0, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
    >
      <div
        className="absolute inset-0 flex flex-col transition-transform duration-250 ease-out"
        style={{ background: 'var(--t-modal-bg)', borderRadius: 'var(--t-container-radius)', transform: animating ? openTransform : closedTransform }}
      >
        {typeof children === 'function' ? children(handleClose) : children}
      </div>
    </div>
  );
}
